import Image from "next/image";
import type { CSSProperties } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLocale } from "@/lib/locale/locale-provider";
import type { AccommodationFeature, AccommodationItem } from "@/lib/sections/accomodations.types";
import styles from "./accommodations.module.css";

type AccommodationCardProps = {
  item: AccommodationItem;
  index?: number;
};

function getFeatureLabel(locale: string, feature: AccommodationFeature) {
  if (locale === "fr") {
    return {
      near: "Pres du lieu",
      family: "Famille",
      parking: "Parking",
      breakfast: "Petit-dejeuner",
      quiet: "Calme",
      kitchen: "Cuisine",
    }[feature];
  }

  if (locale === "en") {
    return {
      near: "Near venue",
      family: "Family stay",
      parking: "Parking",
      breakfast: "Breakfast",
      quiet: "Quiet",
      kitchen: "Kitchen",
    }[feature];
  }

  return {
    near: "Nahe an der Feier",
    family: "Familienfreundlich",
    parking: "Parken",
    breakfast: "Fruehstueck",
    quiet: "Ruhig",
    kitchen: "Kueche",
  }[feature];
}

export function AccommodationCard({
  item,
  index = 0,
}: AccommodationCardProps) {
  const locale = useLocale();
  const websiteTone = item.bookingUrl ? "secondary" : "primary";
  const routeTone = item.bookingUrl || item.websiteUrl ? "secondary" : "primary";
  const actions = [
    item.bookingUrl
      ? {
          href: item.bookingUrl,
          label:
            item.ctaLabel?.trim() ||
            (locale === "fr" ? "Reserver" : locale === "en" ? "Book" : "Buchen"),
          tone: "primary" as const,
        }
      : null,
    item.websiteUrl
      ? {
          href: item.websiteUrl,
          label: locale === "fr" ? "Site web" : locale === "en" ? "Website" : "Website",
          tone: websiteTone,
        }
      : null,
    item.routeUrl
      ? {
          href: item.routeUrl,
          label: locale === "fr" ? "Itineraire" : locale === "en" ? "Map" : "Route",
          tone: routeTone,
        }
      : null,
  ].filter((action): action is { href: string; label: string; tone: "primary" | "secondary" } => Boolean(action));
  const dedupedActions = actions.filter(
    (action, actionIndex) => actions.findIndex((candidate) => candidate.href === action.href) === actionIndex,
  );
  const hasDistance = item.distance.trim().length > 0;
  const displayDistance = hasDistance
    ? item.distance
    : item.description ??
      (locale === "fr" ? "Pres du lieu" : locale === "en" ? "Near the venue" : "Nahe an der Location");
  const supportingText = hasDistance ? item.description : undefined;
  const recommendedBadge =
    locale === "fr" ? "Recommande" : locale === "en" ? "Recommended" : "Empfohlen";

  return (
    <article
      className={styles.card}
      style={{ "--card-index": index } as CSSProperties}
    >
      <div className={styles.media}>
        <Image
          src={item.image}
          alt={item.name}
          fill
          sizes="(max-width: 699px) 100vw, (max-width: 1099px) 50vw, 33vw"
          className={styles.image}
        />

        {item.recommended ? (
          <span className={styles.recommendedBadge}>{recommendedBadge}</span>
        ) : null}
      </div>

      <div className={styles.body}>
        <div className={styles.metaRow}>
          <div className={styles.titleGroup}>
            <h3 className={styles.cardTitle}>{item.name}</h3>
            <p className={styles.distance}>
              <MapPin size={16} className={styles.distanceIcon} aria-hidden="true" />
              <span>{displayDistance}</span>
            </p>
          </div>

          {item.price ? <p className={styles.price}>{item.price}</p> : null}
        </div>

        {supportingText ? (
          <p className={styles.description}>{supportingText}</p>
        ) : null}

        {item.features?.length ? (
          <div className={styles.featureList}>
            {item.features.map((feature) => (
              <span key={`${item.id}-${feature}`} className={styles.feature}>
                {getFeatureLabel(locale, feature)}
              </span>
            ))}
          </div>
        ) : null}

        {dedupedActions.length ? (
          <div className={styles.actionRow}>
            {dedupedActions.map((action) => (
              <a
                key={`${item.id}-${action.href}`}
                href={action.href}
                target="_blank"
                rel="noreferrer"
                className={
                  action.tone === "primary"
                    ? styles.action
                    : `${styles.action} ${styles.actionSecondary}`
                }
              >
                <span>{action.label}</span>
                <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}
